import { type LivestockGenderT } from '@ruminati/types_v2/livestock_shared'

export enum SheepAgeClass {
  // 0-4 months, still on mother
  LAMB_UNWEANED = 'LAMB_UNWEANED',

  // 4-12 months, weaned lambs
  LAMB_WEANER = 'LAMB_WEANER',

  // 12-24 months (between lamb and adult)
  HOGGET = 'HOGGET',

  // Young ewes 7-18 months, not yet joined (depends on system)
  MAIDEN_EWE = 'MAIDEN_EWE',

  // First-time mothers (lambed once, still growing)
  EWE_FIRST_LAMB = 'EWE_FIRST_LAMB',

  // 2+ lambings, mature breeding ewes
  EWE_MATURE = 'EWE_MATURE',

  // Castrated males 12-24 months
  WETHER_12_24M = 'WETHER_12_24M',

  // Older wethers (less common, usually sold by 18-24 months)
  WETHER_24M_PLUS = 'WETHER_24M_PLUS',

  // Breeding rams
  RAM_BREEDING = 'RAM_BREEDING',
}

export const SHEEP_AGE_CLASS_LABELS: Record<SheepAgeClass, string> = {
  [SheepAgeClass.LAMB_UNWEANED]: 'Lambs',
  [SheepAgeClass.LAMB_WEANER]: 'Weaner Lambs',
  [SheepAgeClass.HOGGET]: 'Hoggets',
  [SheepAgeClass.MAIDEN_EWE]: 'Maiden Ewes',
  [SheepAgeClass.EWE_FIRST_LAMB]: 'Ewes - 1st lamb',
  [SheepAgeClass.EWE_MATURE]: 'Mature Ewes',
  [SheepAgeClass.WETHER_12_24M]: 'Wethers',
  [SheepAgeClass.WETHER_24M_PLUS]: 'Wethers (+24 months)',
  [SheepAgeClass.RAM_BREEDING]: 'Ram - breeding',
}

export function getSheepGenderAgeClassLabel(
  gender: LivestockGenderT,
  ageClass: SheepAgeClass,
): string {
  if (ageClass === SheepAgeClass.LAMB_UNWEANED && gender === 'female') {
    return 'Ewe Lambs'
  }
  if (ageClass === SheepAgeClass.LAMB_UNWEANED && gender === 'male') {
    return 'Ram Lambs'
  }
  if (ageClass === SheepAgeClass.LAMB_WEANER && gender === 'female') {
    return 'Ewe Weaners'
  } else if (ageClass === SheepAgeClass.LAMB_WEANER && gender === 'male') {
    return 'Ram Weaners'
  }
  return SHEEP_AGE_CLASS_LABELS[ageClass]
}

export const SHEEP_AGE_CLASS_ORDER = {
  [SheepAgeClass.LAMB_UNWEANED]: 0,
  [SheepAgeClass.LAMB_WEANER]: 1,
  [SheepAgeClass.HOGGET]: 2,
  [SheepAgeClass.MAIDEN_EWE]: 5,
  [SheepAgeClass.EWE_FIRST_LAMB]: 3,
  [SheepAgeClass.WETHER_12_24M]: 4,
  [SheepAgeClass.EWE_MATURE]: 6,
  [SheepAgeClass.WETHER_24M_PLUS]: 7,
  [SheepAgeClass.RAM_BREEDING]: 8,
}

// Rates in $/kg liveweight (lwt), derived from MLA NLRS API 4-month averages (Jan–Apr 2025).
// Source: https://api-mlastatistics.mla.com.au/report/5?indicatorID=<n>
// MLA sheep indicators are in c/kg carcase weight (cwt). Conversion to lwt:
//   $/kg lwt = (c/kg cwt ÷ 100) × dressing percentage (0.47)
// Where no direct indicator exists, a discount/premium multiplier is applied to the closest indicator.
//
// Indicator mapping:
//   LAMB_UNWEANED  — National Restocker Lamb #10 (~717c cwt) × 0.90 discount
//   LAMB_WEANER    — National Light Lamb #6 (~712c cwt)
//   HOGGET         — National Trade Lamb #7 (~787c cwt)
//   MAIDEN_EWE     — National Mutton #11 (~389c cwt) × 1.10 premium
//   EWE_FIRST_LAMB — National Mutton #11 (~389c cwt)
//   EWE_MATURE     — National Mutton #11 (~389c cwt)
//   WETHER_12_24M  — National Trade Lamb #7 (~787c cwt)
//   WETHER_24M_PLUS— National Mutton #11 (~389c cwt)
//   RAM_BREEDING   — National Heavy Lamb #8 (~804c cwt) × 1.10 premium
export const SHEEP_DEFAULT_VALUATION_RATES: Record<SheepAgeClass, number> = {
  [SheepAgeClass.LAMB_UNWEANED]: 3.03, // $/kg lwt - Restocker Lamb #10 × 0.90, cwt→lwt
  [SheepAgeClass.LAMB_WEANER]: 3.35, // $/kg lwt - Light Lamb #6, cwt→lwt
  [SheepAgeClass.HOGGET]: 3.7, // $/kg lwt - Trade Lamb #7, cwt→lwt
  [SheepAgeClass.MAIDEN_EWE]: 2.01, // $/kg lwt - Mutton #11 × 1.10, cwt→lwt
  [SheepAgeClass.EWE_FIRST_LAMB]: 1.83, // $/kg lwt - Mutton #11, cwt→lwt
  [SheepAgeClass.EWE_MATURE]: 1.83, // $/kg lwt - Mutton #11, cwt→lwt
  [SheepAgeClass.WETHER_12_24M]: 3.7, // $/kg lwt - Trade Lamb #7, cwt→lwt
  [SheepAgeClass.WETHER_24M_PLUS]: 1.83, // $/kg lwt - Mutton #11, cwt→lwt
  [SheepAgeClass.RAM_BREEDING]: 4.16, // $/kg lwt - Heavy Lamb #8 × 1.10, cwt→lwt
}
